import { deleteCookie } from "@/lib/cookies"
import { defineStore } from "pinia"
import { computed, ref } from "vue"

import { getMyAdminUser as fetchMyAdminUser, setAdminUserTeamspace, disconnect } from "@/api/core"
import { getCountryConf } from "@/constants/country"
import amplitudeWrapper from "@/lib/amplitudeWrapper"
import { AdminUser, CountryConf, CountryConfKey, Teamspace } from "@/models/core"

export const useAuthStore = defineStore("auth", () => {
    const adminUser = ref<AdminUser>()
    const teamspace = ref<Teamspace>()
    const isFrenchAdminUser = computed<boolean>(() => {
        return !adminUser.value?.country || adminUser.value?.country === "fr"
    })
    const setCurrentTeamspace = async (teamspacePayload: Teamspace): Promise<AdminUser | undefined> => {
        if (!adminUser.value) return

        await setAdminUserTeamspace(adminUser.value.id, teamspacePayload)
        teamspace.value = teamspacePayload
        adminUser.value = {
            ...adminUser.value,
            ...teamspace.value
        }
        return adminUser.value
    }

    const getMyAdminUser = async (): Promise<AdminUser> => {
        try {
            const adminUserResponse: AdminUser = await fetchMyAdminUser()
            adminUser.value = adminUserResponse
            teamspace.value = {
                carpool_app: adminUserResponse.carpool_app,
                country: adminUserResponse.country
            }
            amplitudeWrapper.init(adminUser.value)
            return adminUserResponse
        } catch (err) {
            adminUser.value = undefined
            throw err
        }
    }

    const logout = async (): Promise<void> => {
        localStorage.clear()
        deleteCookie("kadmin_login")
        deleteCookie(import.meta.env.VITE_AUTH_TOKEN_LABEL)
        deleteCookie(import.meta.env.VITE_AUTH_TOKEN_LABEL_TO_DEPRECATE)

        try {
            await disconnect()
        } finally {
            window.open("/admin/login", "_self")
        }
    }

    const country = computed<CountryConfKey>(() => adminUser.value?.country || "fr")
    const countryConf = computed<CountryConf>(() => getCountryConf(country.value, adminUser.value?.carpool_app))

    return {
        adminUser,
        teamspace,
        isFrenchAdminUser,
        setCurrentTeamspace,
        logout,
        getMyAdminUser,
        country,
        countryConf
    }
})
